
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { getTelegramNotificationHistory, resendTelegramNotification } from '@/services/telegramNotificationService';

interface TelegramNotification {
  id: string;
  message: string;
  created_at: string;
  success: boolean;
  notification_type: string;
  recipient_type: string;
  recipient_id: string;
  error_message?: string;
  procurement_id?: string;
}

export function TelegramNotificationHistory() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<TelegramNotification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [resendingId, setResendingId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchNotificationHistory();
    }
  }, [user]);

  const fetchNotificationHistory = async () => {
    setIsLoading(true);
    try {
      const data = await getTelegramNotificationHistory(50);
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async (notificationId: string) => {
    setResendingId(notificationId);
    try {
      await resendTelegramNotification(notificationId);
      // Refresh the list after resending
      fetchNotificationHistory();
    } finally {
      setResendingId(null);
    }
  };

  const handleRefresh = () => {
    fetchNotificationHistory();
  };

  const formatNotificationType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Telegram Notification History</CardTitle>
          <CardDescription>Recent notifications sent via Telegram</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Telegram Notification History</CardTitle>
          <CardDescription>Recent notifications sent via Telegram</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="sent">
          <TabsList className="mb-4">
            <TabsTrigger value="sent">Sent</TabsTrigger>
            <TabsTrigger value="failed">Failed</TabsTrigger>
          </TabsList>
          
          <TabsContent value="sent">
            {notifications.filter(n => n.success).length === 0 ? (
              <div className="text-center p-8">
                <p className="text-muted-foreground">
                  No notification history available. Once notifications are sent, they will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications
                  .filter(n => n.success)
                  .map(notification => (
                    <div key={notification.id} className="border p-4 rounded-md">
                      <div className="flex justify-between">
                        <Badge variant="outline">{formatNotificationType(notification.notification_type)}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center mt-1 text-xs text-muted-foreground">
                        <Badge variant="outline" className="mr-2">
                          {notification.recipient_type}
                        </Badge>
                        ID: {notification.recipient_id.substring(0, 10)}...
                      </div>
                      <p className="mt-2 text-sm">{notification.message}</p>
                    </div>
                  ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="failed">
            {notifications.filter(n => !n.success).length === 0 ? (
              <div className="text-center p-8">
                <p className="text-muted-foreground">No failed notifications.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications
                  .filter(n => !n.success)
                  .map(notification => (
                    <div key={notification.id} className="border border-red-200 bg-red-50 p-4 rounded-md">
                      <div className="flex justify-between">
                        <Badge variant="destructive">{formatNotificationType(notification.notification_type)}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center mt-1 text-xs text-muted-foreground">
                        <Badge variant="outline" className="mr-2">
                          {notification.recipient_type}
                        </Badge>
                        ID: {notification.recipient_id.substring(0, 10)}...
                      </div>
                      <p className="mt-2 text-sm">{notification.message}</p>
                      {notification.error_message && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle>Error</AlertTitle>
                          <AlertDescription>{notification.error_message}</AlertDescription>
                        </Alert>
                      )}
                      <div className="mt-3 flex justify-end">
                        <Button 
                          variant="secondary" 
                          size="sm" 
                          onClick={() => handleResend(notification.id)} 
                          disabled={!!resendingId}
                        >
                          {resendingId === notification.id ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Resending...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Resend
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default TelegramNotificationHistory;
